<section class="notif-page">
  <div
    class="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-3">
    <div>
      <h3 class="title mb-1">Notification Management</h3>
      <p class="subtitle mb-0">
        Create, customize, and broadcast push notifications to users.
      </p>
    </div>
  </div>

  <!-- Sent Notifications Table -->
  <div
    class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
    <div class="d-flex align-items-center gap-2 flex-wrap">
      <app-search-box
        tooltipText="Search By title and message"
        [value]="searchQuery"
      (search)="onSearch($event)"></app-search-box>
      <button
        class="filters-btn"
        (click)="toggleFilters()"
        [disabled]="loading">
        <i class="pi pi-filter"></i> Filters
      </button>

      @if (showFiltersDropdown) {
        <p-select
          [options]="filterAudienceOptions"
          [(ngModel)]="selectedFilterAudience"
          optionLabel="label"
          optionValue="value"
          placeholder="Audience"
          (onChange)="onSearch()"
          [disabled]="loading"
          class="filter-select">
        </p-select>

        <p-select
          [options]="filterDeliveryTypeOptions"
          [(ngModel)]="selectedFilterDeliveryType"
          optionLabel="label"
          optionValue="value"
          placeholder="Delivery Type"
          (onChange)="onSearch()"
          [disabled]="loading"
          class="filter-select">
        </p-select>

        <p-select
          [options]="filterStatusOptions"
          [(ngModel)]="selectedFilterStatus"
          optionLabel="label"
          optionValue="value"
          placeholder="Status"
          (onChange)="onSearch()"
          [disabled]="loading"
          class="filter-select">
        </p-select>
      }

      @if (hasSelection()) {
        <button
          class="btn btn-icon btn-delete-bulk"
          (click)="deleteSelectedNotifications()"
          title="Delete Selected">
          <i class="pi pi-trash" style="font-size: 1.25rem"></i>
        </button>
      }
    </div>

    <div class="d-flex align-items-center gap-3 ms-auto">
      <p-button
        label="Create Notification"
        icon="pi pi-plus"
        (onClick)="showCreateModal = true"
      class="send-btn"></p-button>
      <div
        class="d-flex align-items-center gap-2 border rounded px-1 header-icon-actions-wrapper">
        <button
          type="button"
          class="btn btn-icon p-1 header-icon-action-btn"
          (click)="fetchNotifications()"
          title="Refresh">
          <i
            class="pi pi-refresh"
          style="font-size: 1.25rem; color: #6b7280"></i>
        </button>
        <button
          type="button"
          class="btn btn-icon p-1 tooltip-wrap header-icon-action-btn"
          (click)="toggleIncludeDeleted()">
          <i
            class="pi pi-asterisk"
            style="font-size: 1.25rem"
          [style.color]="includeDeleted() ? '#ec4899' : '#6b7280'"></i>
          <span class="tooltip-box">Include Mark Deleted</span>
        </button>
      </div>
    </div>
  </div>

  <div class="table-card table-container" style="position: relative">
    @if (loading) {
      <div
        class="loading-overlay"
        style="
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        ">
        <app-spinner-loader [width]="35" />
      </div>
    }

    <table class="notif-table user-table" style="width: 100%">
      <thead>
        <tr>
          <th class="checkbox-col" width="32">
            <input
              type="checkbox"
              [checked]="allSelected()"
              (change)="onToggleSelectAll()" />
            </th>
            <th class="sortable" (click)="onSort('title')">
              Title
              <span
                class="sort-indicator"
                [class.active]="currentSortField() === 'title'"
                >{{ getSortIcon("title") }}</span
                >
              </th>
              <th class="sortable" (click)="onSort('createdAt')">
                Date
                <span
                  class="sort-indicator"
                  [class.active]="currentSortField() === 'createdAt'"
                  >{{ getSortIcon("createdAt") }}</span
                  >
                </th>
                <th class="sortable" (click)="onSort('deliveryType')">
                  Delivery Type
                  <span
                    class="sort-indicator"
                    [class.active]="currentSortField() === 'deliveryType'"
                    >{{ getSortIcon("deliveryType") }}</span
                    >
                  </th>
                  <th class="sortable" (click)="onSort('audience')">
                    Audience
                    <span
                      class="sort-indicator"
                      [class.active]="currentSortField() === 'audience'"
                      >{{ getSortIcon("audience") }}</span
                      >
                    </th>

                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @if (allNotifications.length === 0) {
                    <tr>
                      <td colspan="4" class="no-data-cell">No notifications found</td>
                    </tr>
                  } @else {
                    @for (notif of allNotifications; track notif._id) {
                      <tr
                        [ngClass]="{ 'deleted-row': !!notif.deletedAt }"
                        [class.selected]="isSelected(notif)">
                        <td class="checkbox-col">
                          <input
                            type="checkbox"
                            [checked]="isSelected(notif)"
                            (change)="onToggleSelect(notif)" />
                          </td>
                          <td>
                            <div class="d-flex align-items-center gap-2">
                              <span style="font-weight: normal">{{ notif.title }}</span>
                            </div>
                          </td>
                          <td class="date-col">
                            {{ notif.createdAt | date: "mediumDate" }}
                          </td>
                          <td class="date-col text-capitalize">{{ notif.deliveryType }}</td>
                          <td>
                            <span
                              class="audience-badge"
                              [ngClass]="getAudienceClass(notif.audience)">
                              {{ getAudienceLabel(notif.audience) }}
                            </span>
                            @if (notif.audience === "single") {
                              <div class="text-xs text-muted mt-1" style="font-size: 11px">
                                {{ notif.singleUserPhone || notif.singleUserEmail }}
                              </div>
                            }
                          </td>
                          <td class="text-center">
                            <button class="btn btn-view" (click)="viewNotification(notif)">
                              View
                            </button>
                          </td>
                        </tr>
                      }
                    }
                  </tbody>
                </table>

                <div class="pagination-section">
                  <!-- LEFT SIDE -->
                  <span class="pagination-info">
                    Showing <strong>{{ showingFrom }}</strong> to
                    <strong>{{ showingTo }}</strong> of
                    <strong>{{ totalCount }}</strong> Notifications
                  </span>

                  <!-- RIGHT SIDE -->
                  <div class="pagination-controls">
                    <div class="page-size-wrap">
                      <span class="page-size-label">Page Size:</span>
                      <select
                        class="page-size-select"
                        [value]="pageSize"
                        [disabled]="loading"
                        (change)="onPageSizeChange($event)">
                        @for (size of pageSizes; track size) {
                          <option [value]="size">
                            {{ size }}
                          </option>
                        }
                      </select>
                    </div>

                    <button
                      class="pag-btn"
                      [disabled]="currentPage === 1 || loading"
                      (click)="prevPage()">
                      &lt; Prev
                    </button>

                    <button
                      class="pag-btn"
                      [disabled]="currentPage === totalPages || loading"
                      (click)="nextPage()">
                      Next &gt;
                    </button>
                  </div>
                </div>
              </div>

              <!-- View Notification Modal -->
              @if (showViewModal && selectedNotification) {
                <div class="modal-overlay" (click)="closeViewModal()">
                  <div class="modal-card" (click)="$event.stopPropagation()">
                    <div class="modal-header-gradient">
                      <div class="modal-title-row">
                        <h5>Notification Details</h5>
                        @if (selectedNotification.deletedAt) {}
                        </div>
                        <button class="modal-close-btn" (click)="closeViewModal()">
                          <i class="pi pi-times"></i>
                        </button>
                      </div>

                      <div class="modal-body">
                        <div class="detail-row">
                          <span class="label">TITLE</span>
                          <span class="value fw-semibold">{{
                            selectedNotification.title
                          }}</span>
                        </div>
                        <div class="detail-row">
                          <span class="label">AUDIENCE</span>
                          <span class="value">
                            <span
                              class="audience-badge"
                              [ngClass]="getAudienceClass(selectedNotification.audience)">
                              {{ getAudienceLabel(selectedNotification.audience) }}
                            </span>
                          </span>
                        </div>
                        @if (selectedNotification.audience === "single") {
                          <div class="detail-row">
                            <span class="label">{{
                              selectedNotification.singleUserPhone ? "PHONE" : "EMAIL"
                            }}</span>
                            <span class="value fw-semibold">{{
                              selectedNotification.singleUserPhone ||
                              selectedNotification.singleUserEmail
                            }}</span>
                          </div>
                        }
                        <div class="detail-row">
                          <span class="label">Delivery Type</span>
                          <span class="value">{{ selectedNotification.deliveryType }}</span>
                        </div>
                        <div class="detail-row">
                          <span class="label">DATE</span>
                          <span class="value">{{
                            selectedNotification.createdAt | date: "mediumDate"
                          }}</span>
                        </div>
                        @if (selectedNotification.deletedAt) {
                          <div class="detail-row">
                            <span class="label text-danger">DELETED ON</span>
                            <span class="value text-danger">{{
                              selectedNotification.deletedAt | date: "mediumDate"
                            }}</span>
                          </div>
                        }
                        <div
                          class="detail-row"
                          style="flex-direction: column; align-items: flex-start; gap: 6px">
                          <span class="label">MESSAGE</span>
                          <div
                            class="value message-box"
              style="
                background: #f9fafb;
                padding: 12px;
                border-radius: 8px;
                border: 1px solid #f0f0f0;
                width: 100%;
                font-size: 13px;
                line-height: 1.5;
                color: #4b5563;
                white-space: pre-wrap;
              ">
                            {{ selectedNotification.message }}
                          </div>
                        </div>
                      </div>
                      <div
                        class="modal-footer d-flex justify-content-end gap-2 p-3 border-top">
                        <p-button
                          label="Resend"
                          icon="pi pi-send"
                          severity="primary"
                          [disabled]="loading || !!selectedNotification.deletedAt"
                        (onClick)="resendNotification()"></p-button>
                      </div>
                    </div>
                  </div>
                }
                @if (showCreateModal) {
                  <app-create-notification-modal
                    (close)="showCreateModal = false"
                    (success)="onNotificationSent()">
                  </app-create-notification-modal>
                }
              </section>
